import React from "react";
import Select from "react-select";
import { sriLankanDistricts } from "../../../shared/utils/constants";

const districtOptions = sriLankanDistricts.map((district) => ({
  value: district,
  label: district,
}));

// Makes the district picker match .ui-input / .ui-select.
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 42,
    borderRadius: 8,
    borderColor: state.isFocused ? "var(--main-600)" : "var(--gray-100, #e6e6e6)",
    boxShadow: state.isFocused ? "0 0 0 3px var(--main-50)" : "none",
    fontSize: 14,
    "&:hover": { borderColor: state.isFocused ? "var(--main-600)" : "var(--gray-100, #e6e6e6)" },
  }),
  placeholder: (base) => ({ ...base, color: "var(--gray-400, #999)" }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    backgroundColor: state.isSelected ? "var(--main-600)" : state.isFocused ? "var(--main-50)" : "#fff",
    color: state.isSelected ? "#fff" : "var(--neutral, #121535)",
  }),
  menu: (base) => ({ ...base, zIndex: 5 }),
};

const TextField = ({ id, label, name, type = "text", optional, value, onChange, required }) => (
  <div className="ui-field">
    <label className="ui-label" htmlFor={id}>
      {label}
      {optional && <span className="ui-label__optional"> (optional)</span>}
    </label>
    <input
      id={id}
      type={type}
      name={name}
      className="ui-input"
      value={value}
      onChange={onChange}
      required={required}
    />
  </div>
);

const PhoneField = ({ id, label, name, value, onChange, error, errorText }) => (
  <div className="ui-field">
    <label className="ui-label" htmlFor={id}>{label}</label>
    <input
      id={id}
      type="tel"
      name={name}
      className={`ui-input${error ? " is-invalid" : ""}`}
      value={value}
      onChange={onChange}
      required
      maxLength={10}
      minLength={10}
      pattern="0[0-9]{9}"
    />
    {error && <p className="ui-error">{errorText}</p>}
  </div>
);

// Address fields shared by the account and checkout "add address" dialogs.
// Each dialog keeps its own state, validation and submit; this only renders the form.
const AddressForm = ({
  formId,
  formData,
  onChange,
  onDistrictChange,
  onSubmit,
  phoneError,
  phone2Error,
  districtPlaceholder,
  districtRequired,
}) => {
  const field = (name) => ({
    id: `${formId}-${name}`,
    name,
    value: formData[name],
    onChange,
  });

  return (
    <form id={formId} className="ui-form" onSubmit={onSubmit}>
      <div className="ui-row">
        <TextField {...field("firstName")} label="First name" required />
        <TextField {...field("lastName")} label="Last name" required />
      </div>
      <TextField {...field("address1")} label="Address line 1" required />
      <TextField {...field("address2")} label="Address line 2" optional />
      <div className="ui-row">
        <TextField {...field("city")} label="City" required />
        <div className="ui-field">
          <label className="ui-label" htmlFor={`${formId}-district`}>District</label>
          <Select
            inputId={`${formId}-district`}
            options={districtOptions}
            value={districtOptions.find((opt) => opt.value === formData.state) || null}
            onChange={onDistrictChange}
            isClearable
            placeholder={districtPlaceholder}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={selectStyles}
            menuPlacement="auto"
            required={districtRequired}
          />
        </div>
      </div>
      <div className="ui-row">
        <TextField {...field("postCode")} label="Post code" optional />
        <TextField {...field("email")} label="Email" type="email" required />
      </div>
      <div className="ui-row">
        <PhoneField
          {...field("phone")}
          label="Phone"
          error={phoneError}
          errorText="Phone must start with 0 and contain exactly 10 digits."
        />
        <PhoneField
          {...field("phone2")}
          label="Phone 2"
          error={phone2Error}
          errorText="Phone 2 must start with 0 and contain exactly 10 digits."
        />
      </div>
    </form>
  );
};

export default AddressForm;
